import type {
  AppCopyContentNode,
  AppCopyExportOptions,
  AppCopyOrigin,
  AppCopyPayload,
  AppCopySourceCatalog,
  AppCopySourceReference,
  AppCopyValue,
} from './appCopyTypes';

export type { AppCopySourceCatalog } from './appCopyTypes';

const COPY_ATTRIBUTES = ['aria-label', 'alt', 'placeholder', 'title'] as const;
const EXCLUDED_TAGS = new Set(['script', 'style', 'noscript', 'template']);

function normalizeText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

type SourceMatch = {
  readonly text: string;
  readonly templatePattern: RegExp | null;
  readonly reference: AppCopySourceReference;
};

function createTemplatePattern(template: string): RegExp {
  const escapedParts = template
    .split(/\$\{[^}]+\}/g)
    .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return new RegExp(`^${escapedParts.join('(.+?)')}$`);
}

function createSourceCopyMatcher(
  catalog: AppCopySourceCatalog,
): (text: string) => readonly AppCopySourceReference[] {
  const matches: SourceMatch[] = [];
  for (const area of catalog.areas) {
    for (const source of area.sources) {
      for (const component of source.components) {
        for (const entry of component.entries) {
          const text = normalizeText(entry.text);
          if (!text) continue;
          matches.push({
            text,
            templatePattern: entry.isTemplate || text.includes('${')
              ? createTemplatePattern(text)
              : null,
            reference: {
              areaId: area.id,
              sourceFile: source.file,
              componentName: component.name,
              entryId: entry.id,
              kind: entry.kind,
              property: entry.property,
              uiPath: entry.uiPath,
            },
          });
        }
      }
    }
  }
  return (text) => matches
    .filter((match) => match.templatePattern?.test(text) ?? match.text === text)
    .map((match) => match.reference);
}

function hasEditableContent(element: Element): boolean {
  if (element instanceof HTMLElement && element.isContentEditable) return true;
  for (let current: Element | null = element; current; current = current.parentElement) {
    const attribute = current.getAttribute('contenteditable');
    if (attribute === null) continue;
    const value = attribute.toLowerCase();
    if (value === 'false') return false;
    if (value === '' || value === 'true' || value === 'plaintext-only') return true;
  }
  return false;
}

type CollectedCopy = {
  readonly text: string;
  readonly origin: AppCopyOrigin;
};

function collectElementCopy(element: Element, document: Document): readonly CollectedCopy[] {
  const copy: CollectedCopy[] = [];
  const tag = element.tagName.toLowerCase();
  const hasEditableText = tag === 'textarea' || hasEditableContent(element);
  if (!hasEditableText) {
    for (const child of Array.from(element.childNodes)) {
      if (child.nodeType !== Node.TEXT_NODE) continue;
      const text = normalizeText(child.textContent ?? '');
      if (text) copy.push({ text, origin: 'text' });
    }
  }
  for (const attribute of COPY_ATTRIBUTES) {
    const text = normalizeText(element.getAttribute(attribute) ?? '');
    if (text) copy.push({ text, origin: attribute });
  }
  if (!element.hasAttribute('aria-label')) {
    const references = element.getAttribute('aria-labelledby')?.split(/\s+/).filter(Boolean) ?? [];
    const text = normalizeText(references
      .map((id) => document.getElementById(id)?.textContent ?? '')
      .join(' '));
    if (text) copy.push({ text, origin: 'aria-labelledby' });
  }
  return copy;
}

function isElementVisible(element: Element, document: Document): boolean {
  if (element.hasAttribute('hidden') || element.getAttribute('aria-hidden') === 'true') return false;
  const style = document.defaultView?.getComputedStyle(element);
  return style?.display !== 'none' && style?.visibility !== 'hidden' && style?.visibility !== 'collapse';
}

function collectCurrentPageContent(
  document: Document,
  sourceCatalog: AppCopySourceCatalog,
): readonly AppCopyContentNode[] {
  const findSources = createSourceCopyMatcher(sourceCatalog);

  function visit(element: Element): AppCopyContentNode | null {
    const tag = element.tagName.toLowerCase();
    if (
      EXCLUDED_TAGS.has(tag)
      || element.getAttribute('data-copy-export-ignore') === 'true'
      || !isElementVisible(element, document)
    ) {
      return null;
    }

    const copy: readonly AppCopyValue[] = collectElementCopy(element, document).map((item) => ({
      ...item,
      sources: findSources(item.text),
    }));
    const children = hasEditableContent(element)
      ? []
      : Array.from(element.children)
          .map(visit)
          .filter((child): child is AppCopyContentNode => child !== null);

    if (copy.length === 0 && children.length === 0) return null;
    return {
      tagName: tag,
      role: element.getAttribute('role'),
      copy,
      children,
    };
  }

  return Array.from(document.body.children)
    .map(visit)
    .filter((child): child is AppCopyContentNode => child !== null);
}

export function buildAppCopyPayload(options: AppCopyExportOptions): AppCopyPayload {
  const location = options.document.defaultView?.location;
  return {
    schemaVersion: 2,
    path: location?.pathname ?? '',
    title: options.document.title,
    content: collectCurrentPageContent(options.document, options.sourceCatalog),
  };
}

function getRouteSlug(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const segment = segments[segments.length - 1] ?? 'page';
  return segment.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'page';
}

export function downloadAppCopyJson(options: AppCopyExportOptions): {
  readonly filename: string;
  readonly blob: Blob;
} {
  const payload = buildAppCopyPayload(options);
  const date = (options.now ?? new Date()).toISOString().slice(0, 10);
  const filename = `pipeex-ui-copy-${getRouteSlug(payload.path)}-${date}.json`;
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = options.document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  options.document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  return { filename, blob };
}
